import AbstractFilterControl from "./AbstractFilterControl";
import NumericFilterControl from "./NumericFilterControl";
import FilterControlFactory from "../filterControlFactory";

import type LayerViewModel from "@esWebAPI/es/map/LayerViewModel";
import type { AndCriteria, Criteria } from "@esWebAPI/es/model/Criteria";
import type { Attribute } from "@esWebAPI/es/model/Feature";
import type FormatterFuncs from "@esWebAPI/es/util/formatterFuncs";

export default class AndFilterControl  extends AbstractFilterControl {
    private filters: AbstractFilterControl[];

    constructor(layerViewModel: LayerViewModel, filters?: AbstractFilterControl[]) {
        super(layerViewModel, "And");

        this.filters = filters || [];
        this.filters.forEach((f) => this.addFilterListeners(f));
    }

    private addFilterListeners(filter) {
        filter.getEventChannel().addListener("onUpdate", () => {
            this.eventChannel.fireEvent("onUpdate")
        });

        filter.getEventChannel().addListener("onDestroy", (f: AbstractFilterControl) => {
            this.removeFilter(f);
        });

        filter.getEventChannel().addListener("onLockChanged", (f: AbstractFilterControl) => {
            this.eventChannel.fireEvent("onLockChanged", f)
        });
    }

    toCriteria(expand?: boolean): Criteria | null {
        const criteria = this.filters.map(f => f.toCriteria(expand)).filter(f => f != null);
        
        if (criteria.length == 0) {
            return null;
        }
        
        if (criteria.length == 1) {
            return criteria[0];
        }
        
        return {
            $type: "And",
            criteria: criteria
        };
    }

    fromCriteria(criteria: AndCriteria, attributes: Attribute[], range, layerModule, analysis, formatterFuncs: FormatterFuncs): JQueryDeferred<AbstractFilterControl> {
        criteria = es.control.filterUtil.groupByAttribute(criteria.criteria);

        let dfds = [];
        criteria.forEach(c => {
            const attrs = es.control.filterUtil.getAttributesForCriteria(attributes, c);
            dfds.push(FilterControlFactory
                .fromCriteria(this.layerViewModel, c, attrs, range, layerModule, analysis, formatterFuncs)
                .then((filter) =>  this.addFilter(filter, true)));
        });

        return $.when.apply($, dfds).then(() => this );
    }

    setRange(range) {
        this.filters.forEach(f => f.setRange(range));
    }

    removeFilter(filter: AbstractFilterControl, silent = false) {
        const index = this.filters.indexOf(filter);

        if (index >= 0) {
            this.filters.splice(index, 1);
        }

        if (this.filters.length === 0) {
            this.destroy();
            return;
        }

        if (!silent) {
            this.eventChannel.fireEvent("onUpdate")
        }
    }

    addFilter(filter: AbstractFilterControl, silent = false) {
        this.filters.push(filter);
        this.addFilterListeners(filter);

        if (!silent) {
            this.eventChannel.fireEvent("onUpdate")
        }
    }

    getFilters() {
        return this.filters;
    }

    hasFilter(filter: AbstractFilterControl): boolean {
        return this.filters.indexOf(filter) >= 0;
    }

    getAttributesNames(): string[] {
        return this.filters.reduce((used, filter) => {
            if (filter.getType() === "And" || filter.getType() === "Or") {
                Array.prototype.push.apply(used, filter.getAttributesNames());
            } else if (filter.getAttrName()) {
                used.push(filter.getAttrName());
            }
            return used;
        }, [] as string[]);
    }

    public getDynamicAttributes(_criteria?: Criteria ): JQueryDeferred<Attribute[]> {
        const dfds = this.filters.reduce((used, filter) => {
            used.push(filter.getDynamicAttributes());
            return used;
        }, [] as JQueryDeferred<Attribute[]>[]);

        return $.when.apply($, dfds).then((...dynamicAttrs: Attribute[][]) => {
            function pushRecursive(fromArray: any[], toArray: any[]) {
                if (fromArray.length === 0) {
                    return;
                }

                fromArray.forEach((element) => {
                    if (Array.isArray(element)) {
                        pushRecursive(element, toArray);
                    } else {
                        toArray.push(element);
                    }
                });
            }

            return dynamicAttrs.reduce((arr: Attribute[], dynamicAttribute) => {
                pushRecursive(dynamicAttribute, arr);
                return arr;
            }, []);
        }) as JQueryDeferred<Attribute[]>;
    }

    setLock(lock: boolean) {
        this.locked = lock;
        this.filters.forEach(f => f.setLock(lock));
        this.eventChannel.fireEvent("onLockChanged", this);
    }

    static supportCriteria(criteria: Criteria, attributes: Attribute[]) {
        return criteria.$type === "And" && !NumericFilterControl.supportCriteria(criteria, attributes);
    }

    static supportAttribute(attrType: string, range) {
        return false;
    }

    static fromCriteria(layerViewModel: LayerViewModel, criteria: Criteria, attributes: Attribute[], range, layerModule, analysis, formatterFuncs: FormatterFuncs) {
        return new AndFilterControl(layerViewModel, []).init().then((filter) => {
            return filter.fromCriteria(criteria, attributes, range, layerModule, analysis, formatterFuncs);
        });
    }

    //es.control.AndFilter = AndFilter;
}
